<template></template>
<script>
    // Tempo real do assistente de IA (docs/mcp/16b-tempo-real.md — parte browser).
    // Reage a dois eventos do canal por-usuário: rascunho pronto e navegação.
    //
    // MULTI-ABA: o canal é por-usuário, então TODAS as abas recebem. Só a aba EM FOCO
    // age ao vivo (navegar / mostrar toast) — abas de fundo não pulam. Se NENHUMA aba
    // está em foco (usuário no Claude Desktop), nada acontece ao vivo.
    //
    // TOAST DURÁVEL: o broadcast/toast é efêmero. A fila server-side (/assistant/pending,
    // TTL 10 min) é a recuperação agnóstica de aba — re-exibida quando o usuário volta o
    // foco ao sistema, para o rascunho não se perder porque o toast passou em branco.
    import StorageUtils from "../../utils/StorageUtils";

    const user = StorageUtils.getUser();
    const assistantChannelId = user && user.id ? `assistant_channel_${user.id}` : '';

    export default {
        name: "AssistantMonitor",
        data() {
            return { pending: [] };
        },
        methods: {
            // Só a aba visível E com foco do SO age ao vivo — impede todas as abas de pularem.
            isActiveTab() {
                const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
                const focused = typeof document === 'undefined'
                    || typeof document.hasFocus !== 'function' || document.hasFocus();
                return visible && focused;
            },
            loadPending() {
                return this.$appHttp.get('/v1/assistant/pending')
                    .then(({data}) => { this.pending = (data && data.items) || []; })
                    .catch(() => {});
            },
            dismiss(id) {
                if (!id) return Promise.resolve();
                this.pending = this.pending.filter(p => p.id !== id);
                return this.$appHttp.post('/v1/assistant/pending/dismiss', {id}).catch(() => {});
            },
            go(path, id, toast) {
                if (path) this.$router.push(path);
                this.dismiss(id);
                if (toast) this.$snotify.remove(toast.id);
            },
            onDraftReady(data) {
                const kind = data.kind === 'invoice' ? 'nota fiscal' : 'contrato';
                this.$snotify.info(
                    data.summary || `Rascunho de ${kind} preparado. Clique para revisar.`,
                    'Preparado por IA',
                    {
                        timeout: 15000,
                        buttons: [{text: 'Revisar', action: (t) => this.go(data.path, data.id, t)}],
                    },
                );
            },
            onNavigate(data) {
                // Consentimento explícito: sem ele, apenas sugere (não navega).
                if (!StorageUtils.assistantCanNavigate()) {
                    this.$snotify.info(
                        `O assistente sugeriu abrir: ${data.path}` + (data.reason ? ` (${data.reason})` : ''),
                        'Sugestão do assistente',
                        {buttons: [{text: 'Abrir', action: (t) => this.go(data.path, data.id, t)}]},
                    );
                    return;
                }

                if (data.path && data.path !== this.$route.fullPath) {
                    this.$router.push(data.path);
                    this.dismiss(data.id);
                    this.$snotify.simple(data.reason || 'Tela aberta pelo assistente.', 'Assistente', {timeout: 4000});
                }
            },
            // Recuperação durável: ao voltar o foco ao sistema, re-exibe as pendências que
            // o toast efêmero pode ter perdido. Toast fixo (timeout 0), sem duplicar.
            surfacePending() {
                if (this._pendingToastId) { this.$snotify.remove(this._pendingToastId); this._pendingToastId = null; }
                if (!this.pending.length || !this.isActiveTab()) return;

                const n = this.pending.length;
                const latest = this.pending[this.pending.length - 1];
                const toast = this.$snotify.info(
                    n === 1
                        ? (latest.summary || 'Você tem uma ação do assistente pendente.')
                        : `Você tem ${n} ações do assistente pendentes.`,
                    'Assistente',
                    {
                        timeout: 0, closeOnClick: false,
                        buttons: [
                            {text: 'Revisar', action: (t) => { this._pendingToastId = null; this.go(latest.path, latest.id, t); }},
                            {text: 'Depois', action: (t) => { this._pendingToastId = null; this.$snotify.remove(t.id); }},
                        ],
                    },
                );
                this._pendingToastId = toast && toast.id;
            },
            onFocus() {
                if (!this.isActiveTab()) return;
                this.loadPending().then(() => this.surfacePending());
            },
        },
        channels: {
            [assistantChannelId]: {
                received(data) {
                    if (!data) return;
                    // A fila server-side é a fonte durável — atualiza sempre.
                    this.loadPending();
                    // Aba de fundo / sem foco NÃO age ao vivo (a fila cobre a recuperação).
                    if (!this.isActiveTab()) return;
                    if (data.type === 'draft_ready') this.onDraftReady(data);
                    else if (data.type === 'navigate') this.onNavigate(data);
                },
            },
        },
        mounted() {
            if (!assistantChannelId) return;
            this.$cable.subscribe({channel: 'AssistantChannel'}, assistantChannelId);

            this._onFocus = () => this.onFocus();
            document.addEventListener('visibilitychange', this._onFocus);
            window.addEventListener('focus', this._onFocus);

            this.loadPending().then(() => this.surfacePending());
        },
        beforeDestroy() {
            if (this._onFocus) {
                document.removeEventListener('visibilitychange', this._onFocus);
                window.removeEventListener('focus', this._onFocus);
            }
        },
    }
</script>
